import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ACTIVE_LINK_STORAGE_KEY } from '../domain/activeProfile';
import {
  applyActiveProfileHeader,
  portalProfilesApi,
  type ClaimPortalProfileBody,
  type PatientDetailCorrectionRequestDto,
  type PortalProfileDto,
  type RequestDetailCorrectionBody,
} from '../infrastructure/portalProfiles.api';
import { profileKeys } from './profiles.query';
import { clearPortalPatientData } from './portalPatientDataScope';

function usePortalProfilesInvalidation() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: profileKeys.all });
}

/** Claim a found profile by verified-contact match, creating an active link. */
export function useClaimProfileCommand() {
  const invalidate = usePortalProfilesInvalidation();
  return useMutation<PortalProfileDto, unknown, ClaimPortalProfileBody>({
    mutationFn: (body) => portalProfilesApi.claim(body),
    onSuccess: invalidate,
  });
}

/** Set the active/last-selected profile and apply it as the per-request header. */
export function useSelectProfileCommand() {
  const invalidate = usePortalProfilesInvalidation();
  const queryClient = useQueryClient();
  return useMutation<string, unknown, string>({
    mutationFn: async (linkId) => {
      const result = await portalProfilesApi.select(linkId);
      await clearPortalPatientData(queryClient);
      applyActiveProfileHeader(result.activeLinkId);
      return result.activeLinkId;
    },
    onSuccess: invalidate,
  });
}

/** Disconnect (revoke) a profile. Clears the active header when the disconnected profile was the active one. */
export function useDisconnectProfileCommand() {
  const invalidate = usePortalProfilesInvalidation();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, string>({
    mutationFn: async (linkId) => {
      const result = await portalProfilesApi.disconnect(linkId);
      if (localStorage.getItem(ACTIVE_LINK_STORAGE_KEY) === linkId) {
        await clearPortalPatientData(queryClient);
        applyActiveProfileHeader(null);
      }
      return result;
    },
    onSuccess: invalidate,
  });
}

/** Ask the clinic to review and correct the active profile's demographic details. Never mutates the patient
 *  record directly — staff apply the fix manually after review. */
export function useRequestDetailCorrectionCommand() {
  return useMutation<PatientDetailCorrectionRequestDto, unknown, RequestDetailCorrectionBody>({
    mutationFn: (body) => portalProfilesApi.requestDetailCorrection(body),
  });
}
